import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, ShieldAlert, CheckCircle2, Loader2 } from "lucide-react";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";
import { submitContact } from "@/lib/scanApi";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "A valid email is required.";
    if (!form.message.trim()) errs.message = "Please include a message.";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setStatus("submitting");
    try {
      await submitContact(form);
      setStatus("success");
      toast.success("Message received", {
        description: "Thanks for reaching out — our team will get back to you.",
      });
    } catch (err) {
      setStatus("idle");
      toast.error("Could not send message", {
        description: err.message || "Please try again in a moment.",
      });
    }
  };

  return (
    <>
      <Seo
        title="Contact — LinkExpln"
        description="Get in touch with the LinkExpln team — questions, partnership inquiries, press or responsible security disclosures."
      />

      <section className="pt-24 pb-16 sm:pt-32 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="ls-overline mb-6">Contact</p>
          <h1 data-testid="contact-heading" className="font-heading font-black tracking-tighter text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            <MaskedLines lines={["TALK TO", "THE TEAM."]} />
          </h1>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-12 gap-12">
          <FadeUp className="md:col-span-5 space-y-10">
            <div>
              <MessageSquare size={24} className="text-[#00C5FE] mb-4" />
              <h2 className="font-heading font-bold text-lg text-white">General inquiries</h2>
              <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
                Questions about LinkExpln, partnerships or press — use the form and
                we&apos;ll get back to you.
              </p>
            </div>
            <div>
              <ShieldAlert size={24} className="text-[#00C5FE] mb-4" />
              <h2 className="font-heading font-bold text-lg text-white">Security disclosures</h2>
              <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
                Found a vulnerability? Mention &ldquo;SECURITY&rdquo; in your subject line and we&apos;ll
                prioritize your message.
              </p>
            </div>
            <div>
              <Mail size={24} className="text-[#00C5FE] mb-4" />
              <h2 className="font-heading font-bold text-lg text-white">Email us</h2>
              <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
                Prefer email? Reach us directly at{" "}
                <a
                  href="mailto:support@linkexpln.com"
                  data-testid="contact-support-email-link"
                  className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200"
                >
                  support@linkexpln.com
                </a>
                . To flag a dangerous URL, the report form reaches the right team faster.
              </p>
            </div>
          </FadeUp>

          <FadeUp delay={0.1} className="md:col-span-7">
            <div className="border border-white/10 bg-[#0A121D] p-6 sm:p-10">
              {status === "success" ? (
                <div data-testid="contact-success-state" className="border border-[#00C5FE]/30 bg-[#00C5FE]/5 p-6 sm:p-8">
                  <div className="flex items-start gap-4">
                    <CheckCircle2 size={24} className="text-[#00C5FE] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-mono-ls text-[11px] tracking-[0.2em] uppercase text-[#00C5FE] mb-2">
                        STATUS: MESSAGE_RECEIVED
                      </p>
                      <h2 className="font-heading font-bold text-lg text-white">
                        Thanks — your message is in.
                      </h2>
                      <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
                        Your message has been securely recorded and our team will get back
                        to you at the email you provided. For anything urgent, you can also
                        email{" "}
                        <a href="mailto:support@linkexpln.com" className="text-[#00C5FE] hover:text-white underline underline-offset-4 transition-colors duration-200">
                          support@linkexpln.com
                        </a>{" "}
                        directly.
                      </p>
                      <button
                        data-testid="contact-reset-button"
                        onClick={() => { setStatus("idle"); setForm({ name: "", email: "", subject: "", message: "", website: "" }); }}
                        className="mt-5 font-mono-ls text-xs tracking-[0.15em] uppercase text-[#00C5FE] hover:text-white transition-colors duration-200 underline underline-offset-4"
                      >
                        Send another message
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="contact-name" className="ls-overline block mb-2">Name</label>
                      <input
                        id="contact-name"
                        data-testid="contact-name-input"
                        value={form.name}
                        onChange={set("name")}
                        className="w-full bg-black border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent"
                        placeholder="Your name"
                      />
                      {errors.name && <p data-testid="contact-name-error" className="mt-2 font-mono-ls text-[11px] text-[#FF3366]" role="alert">{errors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="ls-overline block mb-2">Email</label>
                      <input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className="w-full bg-black border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent"
                        placeholder="you@example.com"
                      />
                      {errors.email && <p data-testid="contact-email-error" className="mt-2 font-mono-ls text-[11px] text-[#FF3366]" role="alert">{errors.email}</p>}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="ls-overline block mb-2">Subject</label>
                    <input
                      id="contact-subject"
                      data-testid="contact-subject-input"
                      value={form.subject}
                      onChange={set("subject")}
                      className="w-full bg-black border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent"
                      placeholder="What is this about?"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="ls-overline block mb-2">Message</label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      rows={5}
                      value={form.message}
                      onChange={set("message")}
                      className="w-full bg-black border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:border-transparent resize-y"
                      placeholder="Tell us more…"
                    />
                    {errors.message && <p data-testid="contact-message-error" className="mt-2 font-mono-ls text-[11px] text-[#FF3366]" role="alert">{errors.message}</p>}
                  </div>
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={set("website")}
                    className="hidden"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={status === "submitting"}
                    className="inline-flex items-center gap-2 btn-brand disabled:opacity-60 text-black font-heading font-bold px-8 py-4 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:ring-offset-2 focus:ring-offset-black"
                  >
                    {status === "submitting" ? (
                      <>
                        <Loader2 size={17} className="animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
